"use client";

import { useState, useEffect } from "react";
import { PhoneCountrySelect } from "./phone-country-select";
import countries from "@/lib/countries.json";

interface PhoneInputProps {
    value?: string;
    onChange?: (value: string) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    className?: string;
    disabled?: boolean;
    error?: boolean;
    placeholder?: string;
}

export function PhoneInput({
    value = "",
    onChange,
    onBlur,
    className,
    disabled = false,
    error = false,
    placeholder = "000 00 000",
}: PhoneInputProps) {
    const [countryCode, setCountryCode] = useState("+47");
    const [phoneNumber, setPhoneNumber] = useState(
        value.replace(/^\+\d+\s*/, "")
    );

    // Handle value prop changes
    useEffect(() => {
        if (value) {
            // Get all dial codes from countries data, sorted by length (longest first)
            const dialCodes = countries
                .map((country) => country.dialCode)
                .filter((code) => code && code !== "")
                .sort((a, b) => b.length - a.length); // Sort by length descending

            // Try to match against known dial codes
            let match = null;
            for (const dialCode of dialCodes) {
                if (value.startsWith(dialCode)) {
                    match = value.match(
                        new RegExp(
                            `^(${dialCode.replace(
                                /[.*+?^${}()|[\]\\]/g,
                                "\\$&"
                            )})(.*)$`
                        )
                    );
                    break;
                }
            }

            // Fallback to generic pattern if no match found
            if (!match) {
                match = value.match(/^(\+\d{1,3})(.*)$/);
            }

            if (match) {
                setCountryCode(match[1]);
                setPhoneNumber(match[2]);
            } else {
                setPhoneNumber(value);
            }
        }
    }, [value]);

    const handlePhoneNumberChange = (newPhoneNumber: string) => {
        setPhoneNumber(newPhoneNumber);
        // Strip spaces and hyphens so the combined number is always compact
        const sanitized = newPhoneNumber.replace(/[\s\-]/g, "");
        const fullNumber = `${countryCode}${sanitized}`;
        onChange?.(fullNumber);
    };

    const handleCountryChange = (newCountryCode: string) => {
        setCountryCode(newCountryCode);
        // Strip spaces and hyphens from the existing local number too
        const sanitized = phoneNumber.replace(/[\s\-]/g, "");
        const fullNumber = `${newCountryCode}${sanitized}`;
        onChange?.(fullNumber);
    };

    return (
        <div className={className}>
            <div className="flex rounded-md mt-3 bg-white outline-1 -outline-offset-1 outline-gray-300 has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2 has-[input:focus-within]:outline-blue-500 dark:bg-white/5 dark:outline-white/10 dark:has-[input:focus-within]:outline-blue-500">
                <div className="grid shrink-0 grid-cols-1 focus-within:relative w-28">
                    <PhoneCountrySelect
                        value={countryCode}
                        onChange={handleCountryChange}
                        disabled={disabled}
                        error={error}
                    />
                </div>
                <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => handlePhoneNumberChange(e.target.value)}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="block min-w-0 grow bg-white py-1.5 pr-3 pl-1 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6 dark:bg-transparent dark:text-white dark:placeholder:text-gray-500"
                />
            </div>
        </div>
    );
}
